// helper
import { cloneDeep } from "lodash";
import set from "lodash/set";
import get from 'lodash/get';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/app';

const TOKEN_KEY = 'LOOPIAPP_Token';

function setCookie(name, value, hours = 2) {
  const expires = new Date(Date.now() + hours * 36e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; Secure; SameSite=Strict`;
}

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1] || null;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; path=/;`;
}

const handler = {
  set: function (obj, prop, value) {
    authService.observers.forEach((ob) => ob({ ...obj, [prop]: value }));
    set(obj, prop, value);
    return true;
  },
};

const normalizeArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const normalizeBusinesses = (value) => {
  const list = normalizeArray(value);
  return list;
};

const areTenantListsEqual = (current = [], next = []) => {
  if (current === next) return true;
  if (current.length !== next.length) return false;

  return current.every((tenant, index) => {
    const nextTenant = next[index];
    return (
      tenant?.id === nextTenant?.id &&
      tenant?.commercialName === nextTenant?.commercialName &&
      tenant?.logoUrl === nextTenant?.logoUrl
    );
  });
};

const authService = {
    state: new Proxy(
        {
            getting: false,
            user: {
                name: null,
                token: null,
                username: null,
                roles: null,
                permissions: null,
                isAuthenticated: false,
                profileUrl: null,
                status: null,
                userId: null,
                tenantId: null,
            },
            businesses: {},
            client: {
              tenants: [],
              tenantSelected: 0,
              isLoadingTenants: false
            },
            catalogs:{
              categories:[]
            }
        },
        handler
    ),
    observers: [],
    subscribe: function (observer) {
        this.observers.push(observer)
    },
    setGetting(val) { this.state.getting = val },
    setToken(val) {
        const user = cloneDeep(this.state.user);
        setCookie(TOKEN_KEY, val);
        user.token = val;
        user.isAuthenticated = true;
        user.profileUrl = null;
        user.status = null;
        user.userId = null;
        user.tenantId = null;
        this.state.getting = true;
        this.state.user = user;
    },
    setLogin(
      token,
      name,
      username,
      roles,
      permissions,
      businesses = [],
      profileUrl = null,
      status = null,
      userId = null,
      tenantId = null,
    ){
        const user = cloneDeep(this.state.user);
        const normalizedRoles = normalizeArray(roles);
        const normalizedPermissions = normalizeArray(permissions);
        const normalizedBusinesses = normalizeBusinesses(businesses);

        user.token = token;
        user.name = name;
        user.username = username;
        user.roles = normalizedRoles;
        user.permissions = normalizedPermissions;
        user.isAuthenticated = true;
        user.profileUrl = profileUrl || null;
        user.status = status || null;
        user.userId = userId || null;
        user.tenantId = tenantId || null;

        setCookie(TOKEN_KEY, token);

        this.state.businesses = normalizedBusinesses.map((business) => cloneDeep(business));
        this.state.getting = false;
        this.state.user = user;
    },
    async logout(options = {}){
        const { skipFirebaseSignOut = false, reload = true } = options;
        const user = cloneDeep(this.state.user);

        if (!skipFirebaseSignOut) {
            try {
                await signOut(auth);
            } catch (error) {
                console.error('Error signing out from Firebase', error);
            }
        }

        deleteCookie(TOKEN_KEY);

        user.token = null;
        user.name = null;
        user.username = null;
        user.roles = null;
        user.isAuthenticated = false;
        user.profileUrl = null;
        user.status = null;
        user.userId = null;
        user.tenantId = null;

        this.state.user = user;
        this.state.businesses = [];

        if (reload) {
            window.location.reload();
        }
    },
    setTenants(tenants, tenantSelected) {
      const currentClient = this.state.client;

      if (
        currentClient.tenantSelected === tenantSelected &&
        areTenantListsEqual(currentClient.tenants, tenants)
      ) {
        return;
      }

      const client = cloneDeep(this.state.client)

      client.tenantSelected = tenantSelected;
      client.tenants = tenants;

      this.state.client = client;
    },
    setLoadingTenants(value) {
    if (this.state.client.isLoadingTenants === value) {
      return;
    }

    const client = cloneDeep(this.state.client)
    client.isLoadingTenants = value;

    this.state.client = client;
    },
    setTenantSelected(value) {
      if (this.state.client.tenantSelected === value) {
        return;
      }

      const client = cloneDeep(this.state.client)
      client.tenantSelected = value;

      this.state.client = client;
    },
    setCategories(categories){
      const catalogs = cloneDeep(this.state.catalogs)
      catalogs.categories = categories;

      this.state.catalogs = catalogs;
    },
    evalLogin(){
        const user = cloneDeep(this.state.user);
        const token = getCookie(TOKEN_KEY);

        if (token === null) return

        const tokenRaw = token.split('.')

        if (tokenRaw.length !== 3) return

        let rawUser = {}
        try {
            rawUser = JSON.parse(atob(tokenRaw[1]))
        } catch (e) {
            return
        }

        const username = get(rawUser, 'username', '') || get(rawUser, 'email', '') || get(rawUser, 'user_id', '') || get(rawUser, 'sub', '');
        const name = get(rawUser, 'name', '') || get(rawUser, 'displayName', '') || username;
        const userId = get(rawUser, 'user_id', null) || get(rawUser, 'sub', null) || null;
        const roles = normalizeArray(get(rawUser, 'roles', []));
        const permissions = normalizeArray(get(rawUser, 'permissions', []));
        const businessesClaim = get(rawUser, 'clients', undefined) !== undefined ? get(rawUser, 'clients', []) : get(rawUser, 'businesses', []);
        const normalizedBusinesses = normalizeBusinesses(businessesClaim);
        const profileUrl = get(rawUser, 'picture', null);

        this.state.businesses = normalizedBusinesses.map((business) => cloneDeep(business));

        user.token = token;
        user.name = name;
        user.username = username;
        user.roles = roles;
        user.permissions = permissions;
        user.isAuthenticated = true
        user.profileUrl = profileUrl || null;
        user.status = null;
        user.userId = userId;
        user.tenantId = null;

        this.state.getting = false;
        this.state.user = user;
    }
}

export default authService
